import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Columns2,
  Columns3,
  CopyPlus,
  FileText,
  GripVertical,
  LayoutGrid,
  LayoutPanelTop,
  PanelLeft,
  PanelRight,
  Settings2,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  ProposalDocumentLayoutPage,
  TemplatePageElementType,
  TemplatePageLayoutMode,
  TemplatePageSectionType,
  TemplatePageType,
} from "../../types";
import { BuilderElementContentEditor } from "./BuilderElementContentEditor";
import { BuilderElementStyleEditor } from "./BuilderElementStyleEditor";
import type { BuilderElementDraft } from "./BuilderSectionElementList";
import { BuilderClosingPageSettings } from "./BuilderClosingPageSettings";
import { BuilderCoverLetterSettings } from "./BuilderCoverLetterSettings";
import { BuilderCoverPageSettings } from "./BuilderCoverPageSettings";
import { BuilderCustomPageSettings } from "./BuilderCustomPageSettings";
import { BuilderLineItemsSettings } from "./BuilderLineItemsSettings";
import { BuilderSignaturePageSettings } from "./BuilderSignaturePageSettings";
import { BuilderStaticPageSettings } from "./BuilderStaticPageSettings";
import type { CanvasSelection } from "./CanvasPageStage";

// ---------------------------------------------------------------------------
// Shared atoms
// ---------------------------------------------------------------------------

function Divider() {
  return <div className="h-px bg-white/8" />;
}

function FieldLabel({ label, hint }: { label: string; hint?: string }) {
  return (
    <div className="mb-1.5">
      <label className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
        {label}
      </label>
      {hint && <p className="mt-0.5 text-[11px] leading-4 text-white/28">{hint}</p>}
    </div>
  );
}

function StyledInput({
  value,
  onChange,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm text-white/90 placeholder:text-white/22 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15 disabled:opacity-40"
    />
  );
}

// Collapsible accordion section
function Accordion({
  title,
  icon,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition hover:bg-white/[0.03]"
      >
        {icon && <span className="text-white/38">{icon}</span>}
        <span className="flex-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
          {title}
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-white/28" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-white/28" />
        )}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb nav
// ---------------------------------------------------------------------------

interface BreadcrumbProps {
  page: string | null;
  section?: string;
  element?: string;
  onClickPage?: () => void;
  onClickSection?: () => void;
}

function Breadcrumb({ page, section, element, onClickPage, onClickSection }: BreadcrumbProps) {
  return (
    <div className="flex flex-wrap items-center gap-1 px-4 py-2.5 text-[11px]">
      {page && (
        <>
          <button
            type="button"
            onClick={onClickPage}
            className={cn(
              "rounded px-1 py-0.5 transition",
              section
                ? "text-white/40 hover:text-white/70"
                : "font-semibold text-white/80",
            )}
          >
            {page}
          </button>
        </>
      )}
      {section && (
        <>
          <ChevronRight className="h-3 w-3 text-white/25" />
          <button
            type="button"
            onClick={onClickSection}
            className={cn(
              "rounded px-1 py-0.5 transition",
              element
                ? "text-white/40 hover:text-white/70"
                : "font-semibold text-white/80",
            )}
          >
            {section}
          </button>
        </>
      )}
      {element && (
        <>
          <ChevronRight className="h-3 w-3 text-white/25" />
          <span className="rounded px-1 py-0.5 font-semibold text-white/80">
            {element}
          </span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel context header
// ---------------------------------------------------------------------------

function ContextHeader({
  icon,
  label,
  sublabel,
  chipLabel,
  chipClass,
}: {
  icon: ReactNode;
  label: string;
  sublabel?: string;
  chipLabel?: string;
  chipClass?: string;
}) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="shrink-0 rounded-xl border border-white/10 bg-white/[0.05] p-2 text-white/55">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white/88">{label}</p>
        {sublabel && (
          <p className="mt-0.5 truncate text-[11px] text-white/40">{sublabel}</p>
        )}
        {chipLabel && (
          <span
            className={cn(
              "mt-1.5 inline-block rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]",
              chipClass ?? "border-white/15 bg-white/[0.04] text-white/45",
            )}
          >
            {chipLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section type + layout helpers
// ---------------------------------------------------------------------------

const SECTION_TYPES: Array<{
  value: TemplatePageSectionType;
  label: string;
  chipClass: string;
}> = [
  { value: "hero", label: "Hero", chipClass: "border-sky-400/25 bg-sky-400/8 text-sky-300" },
  { value: "content", label: "Content", chipClass: "border-slate-400/25 bg-slate-400/8 text-slate-300" },
  { value: "media", label: "Media", chipClass: "border-violet-400/25 bg-violet-400/8 text-violet-300" },
  { value: "services", label: "Services", chipClass: "border-emerald-400/25 bg-emerald-400/8 text-emerald-300" },
  { value: "summary", label: "Summary", chipClass: "border-amber-400/25 bg-amber-400/8 text-amber-300" },
  { value: "footer", label: "Footer", chipClass: "border-pink-400/25 bg-pink-400/8 text-pink-300" },
  { value: "custom", label: "Custom", chipClass: "border-white/15 bg-white/[0.04] text-white/45" },
];

const LAYOUT_MODES: Array<{
  value: TemplatePageLayoutMode;
  label: string;
  icon: ReactNode;
}> = [
  { value: "stack", label: "Stack", icon: <LayoutPanelTop className="h-4 w-4" /> },
  { value: "two_column", label: "2 Col", icon: <Columns2 className="h-4 w-4" /> },
  { value: "three_column", label: "3 Col", icon: <Columns3 className="h-4 w-4" /> },
  { value: "split_left", label: "Split L", icon: <PanelLeft className="h-4 w-4" /> },
  { value: "split_right", label: "Split R", icon: <PanelRight className="h-4 w-4" /> },
  { value: "grid", label: "Grid", icon: <LayoutGrid className="h-4 w-4" /> },
];

const ELEMENT_TYPES: Array<{ value: TemplatePageElementType; label: string }> = [
  { value: "text", label: "Text" },
  { value: "rich_text", label: "Rich Text" },
  { value: "image", label: "Image" },
  { value: "logo", label: "Logo" },
  { value: "divider", label: "Divider" },
  { value: "spacer", label: "Spacer" },
  { value: "badge", label: "Badge" },
  { value: "company_info", label: "Company Info" },
  { value: "project_summary", label: "Project Summary" },
  { value: "service_table", label: "Service Table" },
  { value: "pricing_summary", label: "Pricing Summary" },
  { value: "signature_block", label: "Signature Block" },
  { value: "static_content", label: "Static Content" },
  { value: "custom", label: "Custom" },
];

// ---------------------------------------------------------------------------
// Section type chip helpers
// ---------------------------------------------------------------------------

function pageTypeLabel(type?: string) {
  switch (type) {
    case "cover": return "Cover Page";
    case "cover_letter": return "Cover Letter";
    case "line_items": return "Services & Pricing";
    case "static_page": return "Static Page";
    case "custom": return "Custom Page";
    case "closing": return "Closing Page";
    case "signature": return "Signature Page";
    default: return "Page";
  }
}

function pageTypeDescription(type?: string): string {
  switch (type) {
    case "cover":        return "The first page a client sees — your brand, logo, and proposal headline.";
    case "cover_letter": return "A personalized letter introducing your company and the proposal.";
    case "line_items":   return "Lists every service with quantity, unit price, and totals.";
    case "static_page":  return "A reusable page built from saved brand content — terms, about us, etc.";
    case "custom":       return "A fully flexible page — add any sections and elements you need.";
    case "closing":      return "Thank-you message, contact info, and next steps for the client.";
    case "signature":    return "The approval page where the client reviews and signs the proposal.";
    default:             return "";
  }
}

function pageChipClass(type?: string) {
  switch (type) {
    case "cover": return "border-sky-400/25 bg-sky-400/8 text-sky-300";
    case "cover_letter": return "border-violet-400/25 bg-violet-400/8 text-violet-300";
    case "line_items": return "border-emerald-400/25 bg-emerald-400/8 text-emerald-300";
    case "static_page": return "border-amber-400/25 bg-amber-400/8 text-amber-300";
    case "closing": return "border-pink-400/25 bg-pink-400/8 text-pink-300";
    case "signature": return "border-cyan-400/25 bg-cyan-400/8 text-cyan-300";
    default: return "border-white/15 bg-white/[0.04] text-white/45";
  }
}

function sectionChipClass(type?: string) {
  return SECTION_TYPES.find((s) => s.value === type)?.chipClass
    ?? "border-white/15 bg-white/[0.04] text-white/45";
}

// ---------------------------------------------------------------------------
// Danger zone button
// ---------------------------------------------------------------------------

function DangerButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleClick() {
    if (!confirming) {
      setConfirming(true);
      timerRef.current = setTimeout(() => setConfirming(false), 2500);
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setConfirming(false);
      onClick();
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition disabled:opacity-40",
        confirming
          ? "border-red-500/40 bg-red-500/20 text-red-200"
          : "border-white/8 bg-white/[0.025] text-white/50 hover:border-red-500/25 hover:bg-red-500/10 hover:text-red-300",
      )}
    >
      <Trash2 className="h-4 w-4 shrink-0" />
      {confirming ? `Tap again to confirm` : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// PAGE PANEL
// ---------------------------------------------------------------------------

interface PagePanelProps {
  page: ProposalDocumentLayoutPage;
  onChangeTitle: (title: string) => void;
  onChangePage: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  readOnly: boolean;
}

function PagePanel({
  page,
  onChangeTitle,
  onChangePage,
  onDuplicate,
  onDelete,
  readOnly,
}: PagePanelProps) {
  const [settingsOpen, setSettingsOpen] = useState(true);

  useEffect(() => {
    setSettingsOpen(true);
  }, [page.id]);

  const sectionCount = Array.isArray(page.sections) ? page.sections.length : 0;
  const elementCount = Array.isArray(page.sections)
    ? page.sections.reduce((sum, s) => {
        const rec = s as Record<string, unknown>;
        return sum + (Array.isArray(rec.elements) ? rec.elements.length : 0);
      }, 0)
    : 0;

  function renderSettings() {
    switch (page.type as TemplatePageType) {
      case "cover":
        return <BuilderCoverPageSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      case "cover_letter":
        return <BuilderCoverLetterSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      case "static_page":
        return <BuilderStaticPageSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      case "line_items":
        return <BuilderLineItemsSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      case "closing":
        return <BuilderClosingPageSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      case "signature":
        return <BuilderSignaturePageSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
      default:
        return <BuilderCustomPageSettings page={page} readOnly={readOnly} onChange={onChangePage} />;
    }
  }

  return (
    <div>
      {/* Page context */}
      <ContextHeader
        icon={<FileText className="h-4 w-4" />}
        label={page.title || "Untitled Page"}
        sublabel={`${sectionCount} section${sectionCount === 1 ? "" : "s"} · ${elementCount} element${elementCount === 1 ? "" : "s"}`}
        chipLabel={pageTypeLabel(page.type)}
        chipClass={pageChipClass(page.type)}
      />

      {/* Page type description */}
      {pageTypeDescription(page.type) && (
        <div className="px-4 pb-3">
          <p className="text-[11px] leading-5 text-white/38">{pageTypeDescription(page.type)}</p>
        </div>
      )}

      <Divider />

      {/* Title */}
      <Accordion title="Page title" defaultOpen={true}>
        <StyledInput
          value={page.title ?? ""}
          onChange={onChangeTitle}
          disabled={readOnly}
          placeholder="Page title"
        />
      </Accordion>

      <Divider />

      {/* Settings accordion */}
      <div>
        <button
          type="button"
          onClick={() => setSettingsOpen((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-left transition hover:bg-white/[0.03]"
        >
          <Settings2 className="h-3.5 w-3.5 text-white/38" />
          <span className="flex-1 text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
            Page settings
          </span>
          {settingsOpen ? (
            <ChevronDown className="h-3.5 w-3.5 text-white/28" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-white/28" />
          )}
        </button>
        {settingsOpen && <div className="px-4 pb-4">{renderSettings()}</div>}
      </div>

      <Divider />

      {/* Actions */}
      <div className="space-y-2 px-4 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/28">
          Actions
        </p>
        <button
          type="button"
          onClick={onDuplicate}
          disabled={readOnly}
          className="flex w-full items-center gap-2 rounded-lg border border-white/8 bg-white/[0.025] px-3 py-2.5 text-sm text-white/65 transition hover:border-white/15 hover:bg-white/[0.06] hover:text-white disabled:opacity-40"
        >
          <CopyPlus className="h-4 w-4 shrink-0" />
          Duplicate page
        </button>
        <DangerButton label="Delete page" onClick={onDelete} disabled={readOnly} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION PANEL
// ---------------------------------------------------------------------------

export type SectionDraft = {
  id: string;
  title: string;
  type: TemplatePageSectionType;
  layoutMode: TemplatePageLayoutMode;
  settings: Record<string, string | boolean | number | null>;
  elements: BuilderElementDraft[];
};

interface SectionPanelProps {
  section: SectionDraft;
  onUpdate: (updates: Partial<SectionDraft>) => void;
  onDelete: () => void;
  onSelectElement: (elementId: string) => void;
  readOnly: boolean;
}

function SectionPanel({
  section,
  onUpdate,
  onDelete,
  onSelectElement,
  readOnly,
}: SectionPanelProps) {
  return (
    <div>
      <ContextHeader
        icon={<FileText className="h-4 w-4" />}
        label={section.title || "Untitled Section"}
        sublabel={`${section.elements.length} element${section.elements.length === 1 ? "" : "s"}`}
        chipLabel={section.type}
        chipClass={sectionChipClass(section.type)}
      />

      <Divider />

      {/* Title */}
      <Accordion title="Section title" defaultOpen={true}>
        <StyledInput
          value={section.title}
          onChange={(v) => onUpdate({ title: v })}
          disabled={readOnly}
          placeholder="Section title"
        />
      </Accordion>

      <Divider />

      {/* Section type */}
      <Accordion title="Section type" defaultOpen={true}>
        <div className="grid grid-cols-2 gap-1.5">
          {SECTION_TYPES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={readOnly}
              onClick={() => onUpdate({ type: opt.value })}
              className={cn(
                "rounded-lg border px-2.5 py-2 text-[11px] font-medium transition",
                section.type === opt.value
                  ? opt.chipClass
                  : "border-white/8 bg-white/[0.025] text-white/45 hover:border-white/15 hover:text-white/70",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </Accordion>

      <Divider />

      {/* Layout */}
      <Accordion title="Layout" defaultOpen={true}>
        <div className="grid grid-cols-3 gap-1.5">
          {LAYOUT_MODES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={readOnly}
              onClick={() => onUpdate({ layoutMode: opt.value })}
              className={cn(
                "flex flex-col items-center gap-1.5 rounded-lg border py-2.5 text-[10px] font-medium transition",
                section.layoutMode === opt.value
                  ? "border-blue-500/40 bg-blue-500/10 text-blue-300"
                  : "border-white/8 bg-white/[0.025] text-white/40 hover:border-white/15 hover:text-white/65",
              )}
            >
              <span className={section.layoutMode === opt.value ? "text-blue-300" : "text-white/35"}>
                {opt.icon}
              </span>
              {opt.label}
            </button>
          ))}
        </div>
      </Accordion>

      {/* Elements list */}
      {section.elements.length > 0 && (
        <>
          <Divider />
          <Accordion title={`Elements (${section.elements.length})`} defaultOpen={true}>
            <div className="space-y-1">
              {section.elements.map((el, index) => (
                <button
                  key={el.id}
                  type="button"
                  onClick={() => onSelectElement(el.id)}
                  className="flex w-full items-center gap-2.5 rounded-lg border border-white/8 bg-white/[0.025] px-3 py-2 text-left transition hover:border-white/15 hover:bg-white/[0.05]"
                >
                  <GripVertical className="h-3.5 w-3.5 shrink-0 text-white/22" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-white/75">
                      {el.label || `Element ${index + 1}`}
                    </p>
                    <p className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-white/30">
                      {el.type}
                    </p>
                  </div>
                  <ChevronRight className="h-3 w-3 shrink-0 text-white/25" />
                </button>
              ))}
            </div>
          </Accordion>
        </>
      )}

      <Divider />

      {/* Background */}
      <Accordion title="Background" defaultOpen={false}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="color"
                value={
                  typeof section.settings?.backgroundColor === "string" &&
                  section.settings.backgroundColor.startsWith("#")
                    ? section.settings.backgroundColor
                    : "#ffffff"
                }
                disabled={readOnly}
                onChange={(e) => {
                  const opacity =
                    typeof section.settings?.backgroundOpacity === "number"
                      ? section.settings.backgroundOpacity
                      : 100;
                  onUpdate({
                    settings: {
                      ...section.settings,
                      backgroundColor: e.target.value,
                      backgroundOpacity: opacity,
                    },
                  });
                }}
                className="h-8 w-full cursor-pointer rounded-lg border border-white/10 bg-transparent px-1 disabled:opacity-40"
              />
            </div>
            <button
              type="button"
              disabled={readOnly}
              onClick={() =>
                onUpdate({
                  settings: {
                    ...section.settings,
                    backgroundColor: null,
                    backgroundOpacity: 100,
                  },
                })
              }
              className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-[10px] text-white/45 transition hover:border-white/20 hover:text-white/70 disabled:opacity-30"
            >
              Clear
            </button>
          </div>

          {/* Opacity */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-[11px] text-white/40">Opacity</p>
              <p className="text-[11px] text-white/55">
                {typeof section.settings?.backgroundOpacity === "number"
                  ? section.settings.backgroundOpacity
                  : 100}
                %
              </p>
            </div>
            <input
              type="range"
              min={5}
              max={100}
              step={5}
              disabled={readOnly}
              value={
                typeof section.settings?.backgroundOpacity === "number"
                  ? section.settings.backgroundOpacity
                  : 100
              }
              onChange={(e) =>
                onUpdate({
                  settings: {
                    ...section.settings,
                    backgroundOpacity: Number(e.target.value),
                  },
                })
              }
              className="w-full accent-blue-400 disabled:opacity-30"
            />
          </div>
        </div>
      </Accordion>

      <Divider />

      {/* Actions */}
      <div className="space-y-2 px-4 py-3">
        <DangerButton label="Delete section" onClick={onDelete} disabled={readOnly} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ELEMENT PANEL
// ---------------------------------------------------------------------------

interface ElementPanelProps {
  element: BuilderElementDraft;
  onUpdate: (updates: Partial<BuilderElementDraft>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function ElementPanel({ element, onUpdate, onDelete, readOnly }: ElementPanelProps) {
  return (
    <div>
      <ContextHeader
        icon={<FileText className="h-4 w-4" />}
        label={element.label || element.type}
        sublabel={`Type: ${element.type}`}
      />

      <Divider />

      {/* Label + type */}
      <Accordion title="Element" defaultOpen={true}>
        <div className="space-y-3">
          <div>
            <FieldLabel label="Label" />
            <StyledInput
              value={element.label}
              onChange={(v) => onUpdate({ label: v })}
              disabled={readOnly}
              placeholder="Element label"
            />
          </div>

          <div>
            <FieldLabel label="Type" />
            <select
              value={element.type}
              onChange={(e) => onUpdate({ type: e.target.value as TemplatePageElementType })}
              disabled={readOnly}
              className="w-full rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-sm text-white/90 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/15 disabled:opacity-40"
            >
              {ELEMENT_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[#111]">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Accordion>

      <Divider />

      {/* Content editor — unwrapped, no extra card */}
      <Accordion title="Content" defaultOpen={true}>
        <div className="-mx-4">
          <BuilderElementContentEditor
            element={element}
            readOnly={readOnly}
            onChange={onUpdate}
            className="rounded-none border-0 bg-transparent shadow-none"
          />
        </div>
      </Accordion>

      <Divider />

      {/* Style editor */}
      <Accordion title="Style" defaultOpen={false}>
        <div className="-mx-4">
          <BuilderElementStyleEditor
            element={element}
            readOnly={readOnly}
            onChange={onUpdate}
            className="rounded-none border-0 bg-transparent shadow-none"
          />
        </div>
      </Accordion>

      <Divider />

      {/* Actions */}
      <div className="space-y-2 px-4 py-3">
        <DangerButton label="Delete element" onClick={onDelete} disabled={readOnly} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CanvasRightInspector
// ---------------------------------------------------------------------------

export interface CanvasRightInspectorProps {
  page: ProposalDocumentLayoutPage | null;
  selection: CanvasSelection;
  isOpen: boolean;
  onToggle: () => void;
  onClearSelection: () => void;
  onSelectSection: (sectionId: string) => void;
  onSelectElement: (sectionId: string, elementId: string) => void;
  onChangePageTitle: (title: string) => void;
  onChangePage: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  onDuplicatePage: () => void;
  onDeletePage: () => void;
  onUpdateSection: (sectionId: string, updates: Partial<SectionDraft>) => void;
  onDeleteSection: (sectionId: string) => void;
  onUpdateElement: (
    sectionId: string,
    elementId: string,
    updates: Partial<BuilderElementDraft>,
  ) => void;
  onDeleteElement: (sectionId: string, elementId: string) => void;
  readOnly?: boolean;
}

export function CanvasRightInspector({
  page,
  selection,
  isOpen,
  onToggle,
  onClearSelection,
  onSelectSection,
  onSelectElement,
  onChangePageTitle,
  onChangePage,
  onDuplicatePage,
  onDeletePage,
  onUpdateSection,
  onDeleteSection,
  onUpdateElement,
  onDeleteElement,
  readOnly = false,
}: CanvasRightInspectorProps) {
  const sections = Array.isArray(page?.sections)
    ? (page!.sections as Array<Record<string, unknown>>)
    : [];

  const activeSection =
    selection?.kind === "section" || selection?.kind === "element"
      ? (sections.find((s) => s.id === selection.sectionId) as SectionDraft | undefined)
      : undefined;

  const activeElement =
    selection?.kind === "element" && activeSection
      ? activeSection.elements?.find((e) => e.id === selection.elementId)
      : undefined;

  // Breadcrumb values
  const pageLabel = page?.title || "Page";
  const sectionLabel = activeSection?.title || "Section";
  const elementLabel = activeElement?.label || activeElement?.type || "Element";

  function renderContent() {
    if (!page) {
      return (
        <div className="flex min-h-[200px] items-center justify-center px-4 text-center">
          <p className="text-sm text-white/30">Select a page to inspect.</p>
        </div>
      );
    }

    if (selection?.kind === "element" && activeSection && activeElement) {
      return (
        <ElementPanel
          element={activeElement}
          onUpdate={(updates) =>
            onUpdateElement(activeSection.id, activeElement.id, updates)
          }
          onDelete={() => {
            onDeleteElement(activeSection.id, activeElement.id);
            onClearSelection();
          }}
          readOnly={readOnly}
        />
      );
    }

    if (selection?.kind === "section" && activeSection) {
      return (
        <SectionPanel
          section={activeSection}
          onUpdate={(updates) => onUpdateSection(activeSection.id, updates)}
          onDelete={() => {
            onDeleteSection(activeSection.id);
            onClearSelection();
          }}
          onSelectElement={(elementId) =>
            onSelectElement(activeSection.id, elementId)
          }
          readOnly={readOnly}
        />
      );
    }

    return (
      <PagePanel
        page={page}
        onChangeTitle={onChangePageTitle}
        onChangePage={onChangePage}
        onDuplicate={onDuplicatePage}
        onDelete={onDeletePage}
        readOnly={readOnly}
      />
    );
  }

  return (
    <>
      {/* Toggle tab — always visible, sticks out from the right edge */}
      <button
        type="button"
        onClick={onToggle}
        title={isOpen ? "Collapse inspector" : "Open inspector"}
        className={cn(
          "absolute top-1/2 z-50 -translate-y-1/2 flex h-16 w-7 flex-col items-center justify-center gap-1 rounded-l-xl border border-r-0 border-white/10 bg-[#0c0d0f] text-white/40 shadow-[-6px_0_16px_rgba(0,0,0,0.3)] transition-all duration-200 hover:text-white/70",
          isOpen ? "right-[300px]" : "right-0",
        )}
      >
        {isOpen ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <>
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <ChevronLeft className="h-3 w-3 opacity-50" />
          </>
        )}
      </button>

      {/* Floating panel */}
      <div
        className={cn(
          "absolute inset-y-0 right-0 z-40 flex w-[300px] flex-col overflow-hidden border-l border-white/8 bg-[#0c0d0f] shadow-[-20px_0_60px_rgba(0,0,0,0.35)] transition-transform duration-200 ease-in-out",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        {/* Top bar */}
        <div className="border-b border-white/8">
          <div className="flex items-center justify-between px-4 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/30">
              Inspector
            </p>
            {selection && (
              <button
                type="button"
                onClick={onClearSelection}
                className="text-[10px] text-white/30 transition hover:text-white/60"
              >
                ✕ deselect
              </button>
            )}
          </div>

          {/* Breadcrumb */}
          {page && (
            <Breadcrumb
              page={pageLabel}
              section={activeSection ? sectionLabel : undefined}
              element={activeElement ? elementLabel : undefined}
              onClickPage={onClearSelection}
              onClickSection={
                activeElement && activeSection
                  ? () => onSelectSection(activeSection.id)
                  : undefined
              }
            />
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">{renderContent()}</div>
      </div>
    </>
  );
}
